import { DEFAULT_COLOR_ON_DARK, DEFAULT_COLOR_ON_LIGHT } from 'enums/canvas';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import { DEG_TO_RAD, RAD_FULL_ROTATION } from 'lib_ts/classes/math.utilities';
import { IDiamondConfig } from 'lib_ts/interfaces/i-diamond-config';
import { ISHHitDetails } from 'lib_ts/interfaces/session-hits/i-hit-details';
import { clamp, isNil } from 'lodash';

const TESTING = false;

const BASE_INFIELD_FT = 90;
const BASE_MIDFIELD_FT = 140;
const BASE_OUTFIELD_FT = 325;
const BASE_ANGLE_RAD = Math.PI / 4;

interface ICoord {
  x: number;
  y: number;
}

const PITCHER_MOUND: ICoord = {
  x: 0,
  y: 59,
};

const BASE_HOME: ICoord = {
  x: 0,
  y: 0,
};

const BASE_1ST: ICoord = {
  x: Math.cos(BASE_ANGLE_RAD) * BASE_INFIELD_FT,
  y: Math.sin(BASE_ANGLE_RAD) * BASE_INFIELD_FT,
};

// * this is a hack because w/e
const BASE_1ST_MID: ICoord = {
  x: Math.cos(BASE_ANGLE_RAD) * BASE_MIDFIELD_FT,
  y: Math.sin(BASE_ANGLE_RAD) * BASE_MIDFIELD_FT,
};

const BASE_2ND: ICoord = {
  x: 0,
  y: Math.sqrt(2) * BASE_INFIELD_FT,
};

const BASE_3RD: ICoord = {
  x: -Math.cos(BASE_ANGLE_RAD) * BASE_INFIELD_FT,
  y: Math.sin(BASE_ANGLE_RAD) * BASE_INFIELD_FT,
};

const BASE_1ST_OF: ICoord = {
  x: Math.cos(BASE_ANGLE_RAD) * BASE_OUTFIELD_FT,
  y: Math.sin(BASE_ANGLE_RAD) * BASE_OUTFIELD_FT,
};

const BASE_3RD_OF: ICoord = {
  x: -Math.cos(BASE_ANGLE_RAD) * BASE_OUTFIELD_FT,
  y: Math.sin(BASE_ANGLE_RAD) * BASE_OUTFIELD_FT,
};

interface IConfig {
  isDark: boolean;
  ftToPx: number;
  minXFt: number;
  maxXFt: number;
  minYFt: number;
  maxYFt: number;
  widthScale: number;
}

export class DiamondCanvas {
  readonly CONFIG: IDiamondConfig;

  constructor(config: IConfig) {
    const deltaXFt = config.maxXFt - config.minXFt;
    const deltaYFt = config.maxYFt - config.minYFt;

    this.CONFIG = {
      isDark: config.isDark,
      colors: {
        default: config.isDark ? DEFAULT_COLOR_ON_DARK : DEFAULT_COLOR_ON_LIGHT,
      },
      canvas: {
        ftToPx: config.ftToPx,
        widthPx: deltaXFt * config.ftToPx,
        heightPx: deltaYFt * config.ftToPx,
        widthScale: config.widthScale,
        aspectRatio: (deltaXFt / deltaYFt).toFixed(1),
      },
      x: {
        minFt: config.minXFt,
        maxFt: config.maxXFt,
        defaultFt: 0,
      },
      y: {
        minFt: config.minYFt,
        maxFt: config.maxYFt,
        defaultFt: 0,
      },
    };
  }

  static makeWidget(config: IConfig) {
    return new DiamondCanvas(config);
  }

  // h launch:
  // - down the middle should look like a straight line from viewer's pov (above)
  // - foul balls should have max curve since they would be moving sideways from viewer's pov
  // * ignores sign of h launch angle since it doesn't matter if the hit when to LF vs RF for this calculation
  static normHLaunch(degree: number, maxDegree: number) {
    // adjust the maxDegree provided to clip more/less extreme values
    return clamp(Math.abs(degree), 0, maxDegree) / maxDegree;
  }

  // v launch:
  // - grounders would be moving flat/along the ground
  // - fly balls should have max curve
  // * negative v launch angles become 0 grounders
  static normVLaunch(degree: number, maxDegree: number) {
    // adjust the maxDegree provided to clip more/less extreme values
    return clamp(degree, 0, maxDegree) / maxDegree;
  }

  // as either launch angle approaches zero, the arc should curve less and resemble a straight line instead
  static getCurvature(hit: { hLaunchDEG: number; vLaunchDEG: number }) {
    // * ~magic~ increase to make the curve more pronounced (for all results)
    const EXAGGERATION = 0.5;

    return (
      EXAGGERATION *
      this.normHLaunch(hit.hLaunchDEG, 45) *
      this.normVLaunch(hit.vLaunchDEG, 20)
    );
  }

  private getShape = (pts: ICoord[]) => {
    const output = new Path2D();

    pts.forEach((p, i) => {
      if (i === 0) {
        output.moveTo(p.x, p.y);
      } else {
        output.lineTo(p.x, p.y);
      }
    });

    output.closePath();

    return output;
  };

  private ftToPxY(yFt: number) {
    return (this.CONFIG.y.maxFt - yFt) * this.CONFIG.canvas.ftToPx;
  }

  private ftToPxX(xFt: number) {
    return (xFt - this.CONFIG.x.minFt) * this.CONFIG.canvas.ftToPx;
  }

  private getScaledWidth(relValue: number) {
    return relValue * this.CONFIG.canvas.widthScale;
  }

  private scaleFtToPx(ft: number) {
    return ft * this.CONFIG.canvas.ftToPx;
  }

  /** lines + home plate */
  drawField(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = this.CONFIG.colors.default;
    ctx.strokeStyle = this.CONFIG.colors.default;

    // infield diamond
    const infield = this.getShape(
      [BASE_HOME, BASE_1ST, BASE_2ND, BASE_3RD].map(
        // convert from ft to pixels
        (ftCoord) => ({
          x: this.ftToPxX(ftCoord.x),
          y: this.ftToPxY(ftCoord.y),
        })
      )
    );

    ctx.lineWidth = this.getScaledWidth(4);
    ctx.stroke(infield);

    // baselines to outfield
    ctx.lineWidth = this.getScaledWidth(4);
    this.drawLine(ctx, {
      fromFt: BASE_HOME,
      toFt: BASE_1ST_OF,
      lineColor: this.CONFIG.colors.default,
      lineWidth: this.getScaledWidth(2),
    });

    ctx.lineWidth = this.getScaledWidth(4);
    this.drawLine(ctx, {
      fromFt: BASE_HOME,
      toFt: BASE_3RD_OF,
      lineColor: this.CONFIG.colors.default,
      lineWidth: this.getScaledWidth(2),
    });

    // midfield arc
    ctx.lineWidth = this.getScaledWidth(4);
    ctx.beginPath();

    // * ~magic~ number
    const rotToBaselineRAD = 0.3;

    ctx.ellipse(
      this.ftToPxX(0), // position x
      this.ftToPxY(PITCHER_MOUND.y), // position y
      BASE_1ST_MID.x, // radius x
      BASE_1ST_MID.x, // radius y
      rotToBaselineRAD,
      Math.PI, // start at 3B
      -2 * rotToBaselineRAD // end at 1B
    );
    ctx.stroke();

    // outfield arc
    ctx.lineWidth = this.getScaledWidth(4);
    ctx.beginPath();
    ctx.ellipse(
      this.ftToPxX(0), // position x
      this.ftToPxY(BASE_1ST_OF.y), // position y
      BASE_1ST_OF.x, //radius x
      BASE_1ST_OF.x * 0.8, // radius y
      0, // no rotation
      Math.PI, // start at 3B
      0 // end at 1B
    );
    ctx.stroke();

    if (TESTING) {
      // * draw the pitcher's mound
      this.drawDot(ctx, PITCHER_MOUND, '#00FF00');

      // * the outfield arc should pass through 410' from home @ CB
      this.drawDot(ctx, { x: 0, y: 410 }, '#00FFFF');
    }
  }

  static canDrawHit(hit: Partial<ISHHitDetails>) {
    if (isNil(hit.distanceFT)) {
      return false;
    }

    if (isNil(hit.vLaunchDEG)) {
      return false;
    }

    const safeHorDEG = hit.hLandingDEG ?? hit.hLaunchDEG;

    if (isNil(safeHorDEG)) {
      return false;
    }

    return true;
  }

  drawHit(
    ctx: CanvasRenderingContext2D,
    hit: Partial<ISHHitDetails>,
    options?: {
      skipArc?: boolean;
      border?: boolean;
      size?: number;
    }
  ) {
    // unknown outcome === '' => use the old logic
    const outcome = hit._mx_outcome || HitHelper.getOutcome(hit);
    const safeColor = HitHelper.getOutcomeColor(
      outcome,
      this.CONFIG.colors.default
    );

    if (isNil(hit.distanceFT)) {
      // should be in canDrawHit
      return;
    }

    if (isNil(hit.vLaunchDEG)) {
      // should be in canDrawHit
      return;
    }

    // * if landing deg was computed (via model), use that over the launch deg
    const safeHorDEG = hit.hLandingDEG ?? hit.hLaunchDEG;

    if (isNil(safeHorDEG)) {
      // should be in canDrawHit
      return;
    }

    // * safeHorDEG === 0, which should point at 2B (which is at 90deg in this frame of ref)
    // * subtract the degree value: negative angle => towards 3B, positive angle => towards 1B
    const safeHorRAD = Math.PI / 2 - safeHorDEG * DEG_TO_RAD;

    // draw an arc from home base to the landing spot for 3D effect
    if (!options?.skipArc) {
      // where the line ends to leave a gap between ball and line
      const lineLocationFt: ICoord = {
        x: Math.cos(safeHorRAD) * (hit.distanceFT - this.getScaledWidth(15)),
        y: Math.sin(safeHorRAD) * (hit.distanceFT - this.getScaledWidth(15)),
      };

      // arc to ball
      const focalPointFt: ICoord = {
        x: Math.cos(safeHorRAD) * hit.distanceFT * 0.5,
        y: Math.sin(safeHorRAD) * hit.distanceFT * 0.5,
      };

      const radiusXPx = this.scaleFtToPx(hit.distanceFT * 0.5);

      const radiusYFactor = DiamondCanvas.getCurvature({
        hLaunchDEG: safeHorDEG,
        vLaunchDEG: hit.vLaunchDEG,
      });

      const ENABLE_SHADOW_LINE = false;

      // straight line to show ball trajectory when the arc is pronounced
      if (ENABLE_SHADOW_LINE && radiusYFactor > 0.15) {
        ctx.setLineDash([15]);
        this.drawLine(ctx, {
          fromFt: { x: 0, y: 0 },
          toFt: lineLocationFt,
          lineColor: safeColor,
          lineWidth: this.getScaledWidth(1),
        });

        // unset dashed lines so subsequent renders don't get dashed lines everywhere
        ctx.setLineDash([]);
      }

      // determines direction of the curve
      const isLeftField = safeHorDEG < 0;

      ctx.beginPath();

      // * ~magic~ increase to stop arc from touching the landing marker
      const BUFFER_RAD = 0;

      ctx.ellipse(
        this.ftToPxX(focalPointFt.x), // position x
        this.ftToPxY(focalPointFt.y), // position y
        radiusXPx, // radius x
        radiusXPx * radiusYFactor, // radius y
        -safeHorRAD,
        isLeftField ? BUFFER_RAD : Math.PI,
        isLeftField ? Math.PI : -BUFFER_RAD
      );

      ctx.strokeStyle = safeColor;
      ctx.lineWidth = this.getScaledWidth(2);
      ctx.stroke();
    }

    const scaledSize = this.getScaledWidth(options?.size || 10);

    // where the ball actually would have landed
    const ballLocationFt: ICoord = {
      x: Math.cos(safeHorRAD) * hit.distanceFT,
      y: Math.sin(safeHorRAD) * hit.distanceFT,
    };

    ctx.beginPath();
    ctx.ellipse(
      this.ftToPxX(ballLocationFt.x),
      this.ftToPxY(ballLocationFt.y),
      scaledSize,
      scaledSize,
      0,
      0,
      RAD_FULL_ROTATION
    );

    ctx.fillStyle = safeColor;

    if (options?.border) {
      ctx.strokeStyle = 'black';
      ctx.lineWidth = this.getScaledWidth(1);
      ctx.stroke();
    }

    ctx.fill();
  }

  // e.g. drawing pitcher's mound for ref
  private drawDot(
    ctx: CanvasRenderingContext2D,
    locationFt: ICoord,
    color: string
  ) {
    const scaledSize = this.getScaledWidth(8);

    // no outline on dot
    ctx.fillStyle = color;

    // draw the dot
    ctx.beginPath();
    ctx.ellipse(
      this.ftToPxX(locationFt.x),
      this.ftToPxY(locationFt.y),
      scaledSize,
      scaledSize,
      0,
      0,
      RAD_FULL_ROTATION
    );
    ctx.fill();
  }

  private drawLine(
    ctx: CanvasRenderingContext2D,
    config: {
      fromFt: ICoord;
      toFt: ICoord;
      lineColor: string;
      lineWidth: number;
    }
  ) {
    ctx.strokeStyle = config.lineColor;
    ctx.lineWidth = this.getScaledWidth(config.lineWidth);

    ctx.beginPath();
    ctx.moveTo(this.ftToPxX(config.fromFt.x), this.ftToPxY(config.fromFt.y));
    ctx.lineTo(this.ftToPxX(config.toFt.x), this.ftToPxY(config.toFt.y));
    ctx.closePath();
    ctx.stroke();
  }
}
